// src/AssetManager/AufFieldDeleteGuardDialog.jsx
import * as React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Box,
  Button,
  Alert,
  Divider,
} from '@mui/material';

/**
 * AUF Field Delete Guard (enterprise-safe)
 * ---------------------------------------------------------------------------
 * Use this dialog ONLY for Asset Upload Form fields.
 *
 * Decision values returned via onAction(action):
 *   - 'archive' : recommended, hides field but keeps it available for mapping
 *   - 'replace' : opens mapping flow (caller decides what “replace” does)
 *   - 'delete'  : deletes immediately (danger)
 *
 * Notes:
 * - No attempt is made to show “X assets affected” (dynamic schema + no index).
 * - Technical details (UFI, promotion reasons) were intentionally removed
 *   from the primary UX to keep the dialog concise and user-focused.
 */
export default function AufFieldDeleteGuardDialog({
  open,
  onClose,
  onAction,
  busy = false,

  // Field info (recommended for UX context)
  fieldLabel = '—',
  fieldType = '—',
  fieldUfi = null, // kept for future extension (not rendered now)

  // Optional: why the warning is higher severity (you compute tier outside)
  // e.g. { promoted: true, role: true, dropdown: true }
  reasons = null, // kept for future extension (not rendered now)

  // Optional: tailor copy for published vs draft AUF
  // true  => affects live library now
  // false => takes effect on publish
  // null  => unknown
  isPublished = null,

  // Optional: retained for API stability; no longer affects UI
  allowDetails = true,
}) {
  const [showDetails, setShowDetails] = React.useState(false);

  // Reset details state whenever the dialog opens (if reintroduced later)
  React.useEffect(() => {
    if (open) setShowDetails(false);
  }, [open]);

  const handleClose = () => {
    if (!busy) onClose?.();
  };

  const handle = (action) => {
    if (busy) return;
    onAction?.(action);
  };

  const prettyType = React.useMemo(() => {
    const t = String(fieldType || '').trim();
    if (!t) return '—';
    if (t === 'single-select') return 'Single select';
    if (t === 'multi-select') return 'Multi select';
    if (t === 'date-picker') return 'Date';
    if (t === 'multi-line-text') return 'Multi-line text';
    if (t === 'urlField') return 'URL';
    return t.charAt(0).toUpperCase() + t.slice(1);
  }, [fieldType]);

  const publishedHint = React.useMemo(() => {
    if (isPublished === true) {
      return 'This change may affect your live Asset Library immediately.';
    }
    if (isPublished === false) {
      return 'This change will take effect after you publish the Asset Upload Form.';
    }
    return null;
  }, [isPublished]);

  // Action-specific loading labels (more precise than "Working…")
  const archiveLabel = busy ? 'Archiving…' : 'Archive (recommended)';
  const replaceLabel = busy ? 'Opening…' : 'Replace / Map';
  const deleteLabel = busy ? 'Deleting…' : 'Delete field permanently';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="auf-field-delete-guard-title"
      aria-describedby="auf-field-delete-guard-description"
      aria-busy={busy ? 'true' : 'false'}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="auf-field-delete-guard-title">
        Remove field from Asset Upload Form?
      </DialogTitle>

      {/* NOTE: no "dividers" here so there is no line above the buttons */}
      <DialogContent>
        {/* High-salience warning first (best practice for risky actions) */}
        {publishedHint && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {publishedHint}
          </Alert>
        )}

        <DialogContentText id="auf-field-delete-guard-description">
          Removing this field can affect existing assets tagged with it and
          Asset Library filters that rely on it.
        </DialogContentText>

        {/* Target summary block (more prominent than the description) */}
        <Box
          sx={{
            mt: 2,
            px: 2,
            py: 1.25,
            borderRadius: 1,
            bgcolor: (t) => t.palette.action.hover,
            border: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 3,
              rowGap: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                flex: '1 1 160px',
                minWidth: 0,
              }}
            >
              Field Label: {fieldLabel}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                flex: '0 0 auto',
                minWidth: 0,
              }}
            >
              Type: {prettyType}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions (best-practice grouping):
          - Primary/safe actions row
          - Destructive action with clear warning and a divider between them */}
      <DialogActions sx={{ px: 2, py: 1.5, display: 'block' }}>
        {/* Row 1: safe actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <Button onClick={handleClose} disabled={busy} variant="outlined">
            Cancel
          </Button>

          <Button
            onClick={() => handle('replace')}
            disabled={busy}
            variant="outlined"
          >
            {replaceLabel}
          </Button>

          <Button
            onClick={() => handle('archive')}
            disabled={busy}
            variant="contained"
            disableElevation
          >
            {archiveLabel}
          </Button>
        </Box>

        {/* Recommended hint under the primary actions */}
        <Box sx={{ mt: 0.75 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic' }}
          >
            Recommended: Archive hides the field while keeping it available for
            review/mapping.
          </Typography>
        </Box>

        {/* Divider between safe actions + hint and destructive section */}
        <Divider sx={{ my: 1.5 }} />

        {/* Destructive warning + action */}
        <Alert severity="error" sx={{ mb: 1 }}>
          Deleting permanently removes this field. This cannot be undone.
        </Alert>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => handle('delete')}
            disabled={busy}
            color="error"
            variant="contained"
            disableElevation
          >
            {deleteLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

AufFieldDeleteGuardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onAction: PropTypes.func,

  busy: PropTypes.bool,

  fieldLabel: PropTypes.string,
  fieldType: PropTypes.string,
  fieldUfi: PropTypes.string,

  reasons: PropTypes.shape({
    promoted: PropTypes.bool,
    role: PropTypes.bool,
    dropdown: PropTypes.bool,
  }),

  isPublished: PropTypes.bool,
  allowDetails: PropTypes.bool,
};
